// ===========================================
// ReferentielsVehicule — Référentiels véhicule (refonte UX)
// ===========================================
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, InputNumber, Button, Grid, Modal, Form, Popconfirm,
    Space, Tooltip, Tag, Tabs, Select, Switch, Empty,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CarOutlined,
    ThunderboltOutlined, AppstoreOutlined, EnvironmentOutlined, UserOutlined,
    DatabaseOutlined, SafetyCertificateOutlined, CheckCircleOutlined,
    StopOutlined, IdcardOutlined,
} from '@ant-design/icons';
import { referentielsVehiculeService } from '../../common/services';
import { formatMontant } from '../../common/utils/format';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './referentiels-vehicule.page.scss';

const { Option } = Select;
const fmt = (v) => formatMontant(v);

const TYPE_CONFIG = {
    energie: {
        label: 'Énergies',
        singular: 'Énergie',
        icon: <ThunderboltOutlined />,
        accent: '#faad14',
        tone: 'orange',
    },
    categorie_vehicule: {
        label: 'Catégories véhicule',
        singular: 'Catégorie véhicule',
        icon: <CarOutlined />,
        accent: '#1890ff',
        tone: 'blue',
    },
    usage_vehicule: {
        label: 'Usages véhicule',
        singular: 'Usage véhicule',
        icon: <EnvironmentOutlined />,
        accent: '#16a34a',
        tone: 'green',
    },
    classe_conducteur: {
        label: 'Classes conducteur',
        singular: 'Classe conducteur',
        icon: <UserOutlined />,
        accent: '#7c3aed',
        tone: 'purple',
    },
    categorie_risque: {
        label: 'Catégories de risque',
        singular: 'Catégorie de risque',
        icon: <SafetyCertificateOutlined />,
        accent: '#eb2f96',
        tone: 'red',
    },
};

const ReferentielsVehicule = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeType, setActiveType] = useState('energie');
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [seedLoading, setSeedLoading] = useState(false);
    const [form] = Form.useForm();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    const cfg = TYPE_CONFIG[activeType];

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await referentielsVehiculeService.getAll({ type: activeType, includeInactive: true });
            const data = Array.isArray(res) ? res : res?.data || [];
            const sorted = [...data].sort((a, b) =>
                (a.nom || '').localeCompare(b.nom || '', undefined, { numeric: true, sensitivity: 'base' })
            );
            setItems(sorted);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchItems(); }, [activeType]);

    const filteredItems = useMemo(() => {
        if (!search) return items;
        const q = search.toLowerCase();
        return items.filter((i) =>
            `${i.nom || ''} ${i.code || ''} ${i.description || ''}`.toLowerCase().includes(q)
        );
    }, [items, search]);

    const stats = useMemo(() => {
        const total = items.length;
        const actifs = items.filter((i) => i.isActive).length;
        const inactifs = total - actifs;
        return { total, actifs, inactifs };
    }, [items]);

    const handleSave = async (values) => {
        try {
            setModalLoading(true);
            const payload = { ...values, type: activeType };
            if (editingItem) {
                await referentielsVehiculeService.update(editingItem._id, payload);
                toast.success('Élément mis à jour');
            } else {
                await referentielsVehiculeService.create(payload);
                toast.success('Élément créé');
            }
            setModalVisible(false);
            setEditingItem(null);
            form.resetFields();
            fetchItems();
        } catch (error) {
            toast.error(error.message || 'Erreur');
        } finally {
            setModalLoading(false);
        }
    };

    const handleEdit = (record) => {
        setEditingItem(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await referentielsVehiculeService.delete(id);
            toast.success('Élément supprimé');
            fetchItems();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleSeed = async () => {
        try {
            setSeedLoading(true);
            await referentielsVehiculeService.seed(false);
            toast.success('Données initialisées');
            fetchItems();
        } catch (error) {
            toast.error(error.message || 'Erreur');
        } finally {
            setSeedLoading(false);
        }
    };

    const handleToggleActive = async (record, checked) => {
        try {
            await referentielsVehiculeService.update(record._id, { isActive: checked });
            toast.success(checked ? 'Activé' : 'Désactivé');
            fetchItems();
        } catch (error) {
            toast.error('Erreur lors du changement de statut');
        }
    };

    const openCreateModal = () => {
        setEditingItem(null);
        form.resetFields();
        setModalVisible(true);
    };

    const columns = [
        {
            title: 'Nom',
            dataIndex: 'nom',
            key: 'nom',
            sorter: (a, b) => (a.nom || '').localeCompare(b.nom || ''),
            render: (t) => (
                <div className="ref-cell">
                    <div className="ref-icon" style={{ background: `linear-gradient(135deg, ${cfg.accent}cc, ${cfg.accent})` }}>
                        {cfg.icon}
                    </div>
                    <span className="ref-name">{t || '—'}</span>
                </div>
            ),
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 140,
            render: (t) => t
                ? <Tag color="geekblue" className="ref-code-tag">{t}</Tag>
                : <span className="ref-muted">—</span>,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            responsive: ['md'],
            render: (t) => t || <span className="ref-muted">—</span>,
        },
        ...(activeType === 'categorie_risque' ? [{
            title: 'Montant CEDEAO',
            dataIndex: 'montantCedeao',
            key: 'montantCedeao',
            width: 170,
            align: 'right',
            sorter: (a, b) => (a.montantCedeao || 0) - (b.montantCedeao || 0),
            render: (v) => v != null
                ? <span className="ref-amount">{fmt(v)}</span>
                : <span className="ref-muted">—</span>,
        }] : []),
        {
            title: 'Statut',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 110,
            align: 'center',
            render: (v, record) => (
                <Switch
                    size="small"
                    checked={v}
                    checkedChildren="Actif"
                    unCheckedChildren="Inactif"
                    onChange={(checked) => handleToggleActive(record, checked)}
                />
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            align: 'center',
            fixed: isMobile ? false : 'right',
            render: (_, record) => (
                <Space size={6}>
                    <Tooltip title="Modifier">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            className="ref-action-btn ref-action-edit"
                            aria-label="Modifier"
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Supprimer cet élément ?"
                        description="Cette action est irréversible."
                        okText="Oui"
                        cancelText="Non"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(record._id)}
                    >
                        <Tooltip title="Supprimer">
                            <Button
                                size="small"
                                icon={<DeleteOutlined />}
                                className="ref-action-btn ref-action-delete"
                                aria-label="Supprimer"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const tabItems = Object.entries(TYPE_CONFIG).map(([key, c]) => ({
        key,
        label: (
            <span className="ref-tab-label">
                <span className="ref-tab-icon" style={{ color: c.accent }}>{c.icon}</span>
                <span className="tab-label-text">{c.label}</span>
            </span>
        ),
    }));

    return (
        <div className="referentiels-page">
            {/* Header */}
            <header className="ref-header">
                <div className="ref-header-text">
                    <h1 className="ref-title">
                        <AppstoreOutlined /> Référentiels véhicule
                    </h1>
                    <p className="ref-subtitle">Données de référence pour les contrats automobile</p>
                </div>
                <div className="ref-header-actions">
                    <Tooltip title="Initialiser avec les données par défaut">
                        <Button
                            icon={<DatabaseOutlined />}
                            onClick={handleSeed}
                            loading={seedLoading}
                            className="ref-btn-seed"
                        >
                            {!isMobile && 'Initialiser'}
                        </Button>
                    </Tooltip>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreateModal}
                        className="ref-btn-add"
                    >
                        {!isMobile && `Nouvel(le) ${cfg.singular.toLowerCase()}`}
                    </Button>
                </div>
            </header>

            {/* KPIs */}
            <section className="ref-kpis" aria-label="Indicateurs référentiel">
                <MiniKpiCard
                    icon={cfg.icon}
                    label={`Total ${cfg.label.toLowerCase()}`}
                    value={stats.total}
                    tone={cfg.tone}
                />
                <MiniKpiCard
                    icon={<CheckCircleOutlined />}
                    label="Actifs"
                    value={stats.actifs}
                    tone="green"
                />
                <MiniKpiCard
                    icon={<StopOutlined />}
                    label="Inactifs"
                    value={stats.inactifs}
                    tone="grey"
                />
            </section>

            {/* Carte principale */}
            <Card className="ref-main-card">
                <Tabs
                    activeKey={activeType}
                    onChange={(key) => { setActiveType(key); setSearch(''); }}
                    items={tabItems}
                    className="ref-tabs"
                />

                <div className="ref-toolbar">
                    <Input
                        placeholder={`Rechercher dans ${cfg.label.toLowerCase()}…`}
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        className="ref-search"
                    />
                </div>

                <Table
                    dataSource={filteredItems}
                    columns={columns}
                    rowKey="_id"
                    loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: loading ? (
                            <div style={{ minHeight: 220 }} />
                        ) : (
                            <Empty
                                description={`Aucun élément dans ${cfg.label.toLowerCase()}`}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        ),
                    }}
                    pagination={{
                        pageSize: 15,
                        showTotal: (t) => `${t} élément${t > 1 ? 's' : ''}`,
                        showSizeChanger: true,
                    }}
                />
            </Card>

            {/* Modal créer / modifier */}
            <Modal
                title={
                    <span className="ref-modal-title">
                        <span style={{ color: cfg.accent }}>{cfg.icon}</span>
                        {editingItem ? `Modifier — ${cfg.singular}` : `Nouvel(le) ${cfg.singular.toLowerCase()}`}
                    </span>
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                destroyOnClose
                width={520}
                className="ref-modal"
            >
                <Form form={form} layout="vertical" onFinish={handleSave} className="ref-form">
                    <Form.Item
                        name="nom"
                        label="Nom"
                        rules={[{ required: true, message: 'Le nom est obligatoire' }]}
                        normalize={(v) => v?.toUpperCase()}
                    >
                        <Input
                            autoFocus
                            prefix={cfg.icon}
                            placeholder="Ex : Diesel, Catégorie 1…"
                        />
                    </Form.Item>
                    <Form.Item
                        name="code"
                        label="Code"
                        normalize={(v) => v?.toUpperCase()}
                    >
                        <Input
                            prefix={<IdcardOutlined />}
                            placeholder="Ex : DSL, CAT1, CL1…"
                        />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea rows={2} placeholder="Description optionnelle" />
                    </Form.Item>
                    {activeType === 'categorie_risque' && (
                        <Form.Item
                            name="montantCedeao"
                            label="Montant CEDEAO (FCFA)"
                            rules={[{ required: true, message: 'Le montant CEDEAO est requis' }]}
                        >
                            <InputNumber
                                min={0}
                                style={{ width: '100%' }}
                                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                placeholder="0"
                            />
                        </Form.Item>
                    )}
                    {editingItem && (
                        <Form.Item name="isActive" label="Statut">
                            <Select>
                                <Option value={true}>Actif</Option>
                                <Option value={false}>Inactif</Option>
                            </Select>
                        </Form.Item>
                    )}
                    <div className="ref-form-actions">
                        <Button onClick={() => setModalVisible(false)} className="ref-btn-cancel">
                            Annuler
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={modalLoading}
                            className="ref-btn-submit"
                        >
                            {editingItem ? 'Enregistrer' : 'Créer'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default ReferentielsVehicule;
